import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  ChevronLeft,
  ChevronRight,
  Pencil,
  FileWarning,
  Flag,
  Loader2,
  RefreshCw,
  Search,
  ShieldAlert,
  X,
} from 'lucide-react';
import { Button } from '../../components/ui';
import Swal from "../../utils/apparifySwal";
import ShopSelect from "../../components/ShopSelect";

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'ACTION_TAKEN', label: 'Action Taken' },
  { value: 'DISMISSED', label: 'Dismissed' },
];

const REASON_OPTIONS = [
  { value: '', label: 'All Reasons' },
  { value: 'COUNTERFEIT_OR_FAKE', label: 'Counterfeit or fake items' },
  { value: 'FRAUD_OR_SCAM', label: 'Fraud or scam behavior' },
  { value: 'HARASSMENT_OR_ABUSE', label: 'Harassment or abusive behavior' },
  { value: 'MISLEADING_LISTINGS', label: 'Misleading listings' },
  { value: 'PROHIBITED_ITEMS', label: 'Prohibited items' },
  { value: 'SPAM_OR_MANIPULATION', label: 'Spam or manipulation' },
  { value: 'OTHER', label: 'Other' },
];

const REASON_LABELS = REASON_OPTIONS.reduce((acc, item) => {
  if (item.value) acc[item.value] = item.label;
  return acc;
}, {});

const STATUS_BADGES = {
  OPEN: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/60',
  UNDER_REVIEW: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/60',
  ACTION_TAKEN: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60',
  DISMISSED: 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700',
};

const REVIEWER_NOTES_MAX_LENGTH = 2000;

function getStatusBadge(status) {
  return STATUS_BADGES[String(status || '').toUpperCase()] || STATUS_BADGES.DISMISSED;
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatReason(value) {
  return REASON_LABELS[value] || value || '—';
}

function getApiMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.msg || error?.message || fallback;
}

const AdminReports = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCount, setOpenCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const canManageReports = String(user?.role || '').toUpperCase() === 'ADMIN';

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, pageSize };
      if (statusFilter) params.status = statusFilter;
      if (reasonFilter) params.reasonCode = reasonFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const response = await adminApi.getBusinessReports(params);
      const payload = response?.data || response || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const total = Number(payload.meta?.total || 0);
      const currentPageSize = Number(payload.meta?.pageSize || pageSize);

      setReports(items);
      setTotalPages(Math.max(1, total > 0 ? Math.ceil(total / currentPageSize) : 1));
    } catch (error) {
      console.error('Failed to fetch business reports', error);
      setReports([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, reasonFilter, searchTerm, statusFilter]);

  const fetchOpenCount = useCallback(async () => {
    try {
      const response = await adminApi.getBusinessReportCount();
      setOpenCount(response?.data?.count ?? response?.count ?? 0);
    } catch {
      setOpenCount(0);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    fetchOpenCount();
  }, [fetchOpenCount]);

  const reloadSelectedReport = useCallback(async (reportId) => {
    const response = await adminApi.getBusinessReportDetail(reportId);
    const payload = response?.data || response || null;
    if (payload) {
      setSelectedReport(payload);
      setReviewerNotes(payload?.reviewer_notes || '');
    }
    return payload;
  }, []);

  const openDetail = async (report) => {
    setSelectedReport(report);
    setReviewerNotes(report?.reviewer_notes || '');
    setDetailLoading(true);

    try {
      await reloadSelectedReport(report.id);
    } catch (error) {
      console.error('Failed to fetch business report detail', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchReports(), fetchOpenCount()]);
  }, [fetchOpenCount, fetchReports]);

  const handleUnauthorizedMutation = useCallback(async () => {
    await Swal.fire('Admins only', 'Only admins can change seller-report status or suspend a business from this screen.', 'info');
  }, []);

  const handleMutationError = useCallback(async (error, fallback) => {
    const status = error?.response?.status || error?.status || null;

    if (status === 403) {
      await handleUnauthorizedMutation();
      return;
    }

    if (status === 409) {
      await refreshAll();
      if (selectedReport?.id) {
        try {
          await reloadSelectedReport(selectedReport.id);
        } catch (detailError) {
          console.error('Failed to refresh stale report detail', detailError);
          setSelectedReport(null);
        }
      }

      await Swal.fire('Report already updated', getApiMessage(error, 'This report changed on the server. The page has been refreshed.'), 'info');
      return;
    }

    await Swal.fire('Error', getApiMessage(error, fallback), 'error');
  }, [handleUnauthorizedMutation, refreshAll, reloadSelectedReport, selectedReport?.id]);

  const handleStatusUpdate = async (status) => {
    if (!selectedReport) return;
    if (!canManageReports) {
      await handleUnauthorizedMutation();
      return;
    }

    const result = await Swal.fire({
      title: `Mark report as ${status.toLowerCase().replace('_', ' ')}?`,
      text: status === 'UNDER_REVIEW'
        ? 'This keeps the report open while you investigate.'
        : 'This closes the report for the shopper and admin queue.',
      icon: status === 'DISMISSED' ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      setUpdating(true);
      await adminApi.updateBusinessReportStatus(selectedReport.id, status, reviewerNotes.trim() || null);
      await Swal.fire('Done', 'Report updated successfully.', 'success');
      setSelectedReport(null);
      await refreshAll();
    } catch (error) {
      console.error('Failed to update report status', error);
      await handleMutationError(error, 'Failed to update report status.');
    } finally {
      setUpdating(false);
    }
  };

  const handleSuspendAndResolve = async () => {
    if (!selectedReport?.business_id) return;
    if (!canManageReports) {
      await handleUnauthorizedMutation();
      return;
    }

    const reasonParts = [
      'Reported by users',
      formatReason(selectedReport.reason_code),
      reviewerNotes.trim(),
    ].filter(Boolean);
    const moderationReason = reasonParts.join(' — ');

    const result = await Swal.fire({
      title: 'Suspend this business and close the report?',
      text: 'This uses the existing business moderation flow and marks this report as action taken.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Suspend business',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      setUpdating(true);
      await adminApi.moderateBusiness(selectedReport.business_id, 'SUSPEND', moderationReason || null, null);
      await adminApi.updateBusinessReportStatus(selectedReport.id, 'ACTION_TAKEN', moderationReason || null);
      await Swal.fire('Done', 'Business suspended and report closed.', 'success');
      setSelectedReport(null);
      await refreshAll();
    } catch (error) {
      console.error('Failed to moderate business from report', error);
      await handleMutationError(error, 'Failed to suspend business from this report.');
    } finally {
      setUpdating(false);
    }
  };

  const history = useMemo(() => selectedReport?.history || [], [selectedReport]);

  return (
    <div className="admin-data-panel admin-workspace-panel operations-surface overflow-hidden">
      <div className="theme-panel-divider flex flex-col gap-4 border-b p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Flag className="text-rose-600 dark:text-rose-400" size={22} />
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              Seller Reports
              {openCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-400">
                  {openCount} open
                </span>
              )}
            </h2>
          </div>
          <Button
            onClick={refreshAll}
            icon={RefreshCw}
            loading={loading}
            aria-label="Refresh seller reports"
          >
            Refresh
          </Button>
        </div>

        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px_220px]">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => { setSearchTerm(event.target.value); setPage(1); }}
              placeholder="Search business, slug, or reporter"
              className="theme-input pl-9 pr-3"
            />
          </div>

          <ShopSelect
            value={statusFilter}
            onValueChange={(value) => { setStatusFilter(value); setPage(1); }}
            options={STATUS_OPTIONS}
            ariaLabel="Filter reports by status"
            align="start"
            minPanelWidth={220}
            triggerClassName="min-h-10 w-full rounded-lg px-3 py-2 text-sm"
            panelClassName="py-1"
          />

          <ShopSelect
            value={reasonFilter}
            onValueChange={(value) => { setReasonFilter(value); setPage(1); }}
            options={REASON_OPTIONS}
            ariaLabel="Filter reports by reason"
            align="start"
            minPanelWidth={220}
            triggerClassName="min-h-10 w-full rounded-lg px-3 py-2 text-sm"
            panelClassName="py-1"
          />
        </div>
      </div>

      <div className="operations-table-wrap">
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="inline-block animate-spin h-8 w-8 text-rose-500" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">Loading seller reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No seller reports found.
          </div>
        ) : (
          <table className="admin-data-table w-full text-left text-sm">
            <thead className="theme-table-head">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Business</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Reporter</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Submitted</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {reports.map((report) => (
                <tr key={report.id} className="theme-table-row">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{report.business_name || `Business #${report.business_id}`}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">/{report.business_slug}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{report.reporter_name || report.reporter_email || `User #${report.reporter_user_id}`}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white">{formatReason(report.reason_code)}</div>
                    {report.details && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{report.details}</div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadge(report.status)}`}>
                      {report.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDateTime(report.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openDetail(report)}
                      className="theme-icon-button"
                      title="View Report"
                    >
                      <Pencil size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="admin-table-pagination flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 disabled:opacity-40"
          >
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 disabled:opacity-40"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {selectedReport && (
        <div className="admin-modal-backdrop theme-modal-backdrop overflow-y-auto">
          <div className="admin-action-modal theme-modal-panel max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="admin-modal-header flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Report #{selectedReport.id}</h3>
                <span className={`mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadge(selectedReport.status)}`}>
                  {selectedReport.status}
                </span>
              </div>
              <button onClick={() => setSelectedReport(null)} className="theme-icon-button">
                <X size={24} />
              </button>
            </div>

            {detailLoading ? (
              <div className="admin-modal-body p-8 text-center">
                <Loader2 className="inline-block animate-spin h-6 w-6 text-rose-500" />
              </div>
            ) : (
              <div className="admin-modal-body p-6 space-y-5">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Business</h4>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedReport.business_name || `Business #${selectedReport.business_id}`}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Status: {selectedReport.business_status || '—'}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Reporter</h4>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedReport.reporter_name || selectedReport.reporter_email || `User #${selectedReport.reporter_user_id}`}</p>
                    {selectedReport.reporter_email && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedReport.reporter_email}</p>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Reported Reason</h4>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{formatReason(selectedReport.reason_code)}</div>
                    {selectedReport.details && (
                      <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedReport.details}</p>
                    )}
                  </div>
                </div>

                {selectedReport.active_suspension_id && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/30 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
                      <ShieldAlert size={16} />
                      Business Already Suspended
                    </div>
                    <p className="mt-2 text-sm text-amber-900 dark:text-amber-100">{selectedReport.active_suspension_reason || 'No suspension reason recorded.'}</p>
                    <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">Until: {selectedReport.active_suspension_until ? formatDateTime(selectedReport.active_suspension_until) : 'Indefinite'}</p>
                  </div>
                )}

                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Internal Notes</h4>
                  <textarea
                    value={reviewerNotes}
                    onChange={(event) => setReviewerNotes(event.target.value)}
                    maxLength={REVIEWER_NOTES_MAX_LENGTH}
                    readOnly={!canManageReports}
                    placeholder={canManageReports ? 'Add internal review notes' : 'Admins can add or update internal review notes'}
                    className="theme-textarea min-h-[96px]"
                  />
                  <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-gray-500 dark:text-gray-400">
                    <span>
                      {canManageReports
                        ? 'These notes are stored with the report status decision.'
                        : 'Internal notes are read-only here because report mutations are admin-only.'}
                    </span>
                    <span>{reviewerNotes.length}/{REVIEWER_NOTES_MAX_LENGTH}</span>
                  </div>
                </div>

                {(selectedReport.status === 'OPEN' || selectedReport.status === 'UNDER_REVIEW') && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Admin Actions</h4>
                    {canManageReports ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedReport.status === 'OPEN' && (
                          <button
                            onClick={() => handleStatusUpdate('UNDER_REVIEW')}
                            disabled={updating}
                            className="theme-btn-primary gap-1.5"
                          >
                            <FileWarning size={16} /> Mark Under Review
                          </button>
                        )}
                        <button
                          onClick={() => handleStatusUpdate('DISMISSED')}
                          disabled={updating}
                          className="theme-btn-neutral gap-1.5"
                        >
                          Dismiss Report
                        </button>
                        <button
                          onClick={() => handleStatusUpdate('ACTION_TAKEN')}
                          disabled={updating}
                          className="theme-btn-success gap-1.5"
                        >
                          Mark Action Taken
                        </button>
                        {!selectedReport.active_suspension_id && (
                          <button
                            onClick={handleSuspendAndResolve}
                            disabled={updating}
                            className="theme-btn-danger gap-1.5"
                          >
                            Suspend Business
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800/60 dark:bg-blue-950/30 dark:text-blue-200">
                        Moderators can review seller reports here, but only admins can change report status or suspend a business from this workflow.
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Status History</h4>
                  {history.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No report history recorded.</p>
                  ) : (
                    <div className="space-y-2">
                      {history.map((entry) => (
                        <div key={entry.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3">
                          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {entry.previous_status ? `${entry.previous_status} → ${entry.new_status}` : entry.new_status}
                              </div>
                              {entry.notes && <div className="mt-1 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{entry.notes}</div>}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              <div>{entry.actor_name || entry.actor_email || entry.actor_role}</div>
                              <div>{formatDateTime(entry.created_at)}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="admin-modal-footer theme-panel-footer flex justify-end p-6">
              <button
                onClick={() => setSelectedReport(null)}
                className="theme-btn-neutral"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
